import { Section } from '@/components/builder/inspector-controls';
import { Field, NumberInput, SelectInput, TextInput } from '@/components/builder/inspector-fields';
import { findStep, owningStepList, type SessionStore } from '@personalizehq/editor';
import { surfaceSchema, type SurfaceKind } from '@personalizehq/schemas';
import { PanelBottom, PanelTop, Rows3, Smartphone, Square, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useStore } from 'zustand';

// B3 — THE FIELDS NOBODY COULD AUTHOR.
//
// `step.surface` has existed in the schema since P3 and NO CONTROL HAS EVER SET IT: a step was
// whatever the template happened to make it, and `inline` — a v1 requirement, pinned item 12 — was
// literally unreachable from the dashboard. Same for `step.name` (the strip renders it and offers no
// rename) and `advance` (schema-only).
//
// Home is the inspector's empty-selection state, which until now said "Select a block" and nothing
// else. That space was already the right one: nothing is selected, so the thing you are looking at
// IS the step.

const SURFACES = surfaceSchema.options;

/** Where each surface SITS on the screen, as a glyph. Exhaustive over the union, so a new surface
 *  will not compile until it has one. */
const SURFACE_ICON: Record<SurfaceKind, LucideIcon> = {
  modal: Square,
  bottom_sheet: PanelBottom,
  banner: PanelTop,
  fullscreen: Smartphone,
  inline: Rows3,
};

/** Every step, so the surface picker can tell the truth about the ONE-STEP law below. */
export function StepSection({ session }: { session: SessionStore }): React.JSX.Element {
  const doc = useStore(session, (s) => s.doc);
  const focusedStepId = useStore(session, (s) => s.focusedStepId);
  // F14 note, KEYED BY STEP so it never lingers on a different step (no effect needed to clear it).
  const [surfaceNote, setSurfaceNote] = useState<{ stepId: string; text: string } | null>(null);
  const step = findStep(doc, focusedStepId);

  if (!step) return <p className="p-3 text-[12px] text-muted-foreground">This document has no steps.</p>;

  const edit = (field: string, mutate: (s: NonNullable<typeof step>) => void): void =>
    session.getState().applyChange((draft) => {
      const target = findStep(draft, focusedStepId);
      if (target) mutate(target);
    }, `${focusedStepId}.${field}`);

  // F14 — SWITCHING A SURFACE MUST NOT LEAVE THE ROOT BROKEN. A `fullscreen` step legitimately roots
  // at `height: "fill"` (SPEC §6.1c). Switch it to a sheet and that combination is one the publish
  // gate REJECTS — yet the canvas shows no change at all, because a viewport-tall root makes a sheet's
  // `originY` zero, so it renders at the top looking exactly like the page it just was. So the edit
  // that invalidates the root FIXES it, in the SAME merge key (one ⌘Z undoes both), and says so —
  // never a silent broken document for the schema to catch later. (Third instance of this law; the
  // width-clear and `sizeOptions`-`auto` fixes are the other two.)
  const changeSurface = (surface: SurfaceKind): void => {
    // The one-step law is PER LIST (ADR 0015): a variant is its own flow, so "the other steps" are
    // the ones in the list this step lives in, not the default variant's.
    const listLength = owningStepList(doc, focusedStepId)?.steps.length ?? doc.steps.length;
    if (surface === 'inline' && listLength > 1) {
      setSurfaceNote({
        stepId: focusedStepId,
        text: `Inline content is one step because it lives in a host-app slot. Remove the other ${listLength - 1} steps or start from an inline template.`,
      });
      return;
    }
    let carried = false;
    edit('surface', (s) => {
      const leavingFullscreen = s.surface === 'fullscreen' && surface !== 'fullscreen';
      s.surface = surface;
      // Only `height: fill` is illegal off fullscreen; a `fill` WIDTH is fine on any surface (F4).
      if (leavingFullscreen && s.root.size?.height === 'fill') {
        s.root.size.height = 'hug';
        carried = true;
      }
    });
    setSurfaceNote(
      carried
        ? {
            stepId: focusedStepId,
            text: `The root’s height was changed from \`fill\` to \`hug\` — a ${surface} has no viewport to fill.`,
          }
        : null
    );
  };

  const timeout = step.advance?.find((rule) => rule.when === 'timeout');

  return (
    <>
      <Section title="Step">
        <Field label="name" set>
          <TextInput
            aria-label="step name"
            value={step.name}
            onChange={(name) => edit('name', (s) => void (s.name = name || 'Step'))}
          />
        </Field>

        {/* THE ONE THAT UNBLOCKS v1. An `inline` step is a widget in a slot in the host's own
            screen — so the campaign that carries it must be an `inline` placement, and it must be a
            SINGLE step ("a widget is not a flow" — bundle.ts). Both laws are the campaign's to
            enforce, because only it knows the placement; what this control owes the author is the
            truth about them, said BEFORE they pick, not as a publish rejection afterwards. */}
        <Field label="surface" set>
          <SelectInput
            aria-label="surface"
            value={step.surface}
            options={SURFACES}
            // A surface is a SHAPE, and five words in a dropdown are five words. The glyph is the
            // difference between reading "banner" and seeing where it sits on the screen — and it is
            // the reason this control is a Radix Select and not a native one.
            icons={SURFACE_ICON}
            onChange={changeSurface}
          />
        </Field>
        {surfaceNote?.stepId === focusedStepId ? (
          <p className="text-[11px] text-amber-500">{surfaceNote.text}</p>
        ) : null}
        {step.surface === 'inline' ? (
          <p className="text-[11px] text-muted-foreground">
            A widget in the host&rsquo;s screen. Its campaign needs an <b>inline placement</b> and a
            slot. Add step is disabled until you convert this step to an overlay surface.
          </p>
        ) : null}

        {/* `advance` is a list in the schema, but `timeout` is the only rule it has ever had, so the
            control is one number rather than a rule builder for a union of one. */}
        <Field
          label="auto advance"
          set={timeout !== undefined}
          onUnset={() => edit('advance', (s) => void delete s.advance)}
        >
          <NumberInput
            aria-label="step auto advance"
            min={1}
            step={500}
            value={timeout?.ms}
            onChange={(ms) =>
              edit('advance', (s) => {
                if (ms === undefined) delete s.advance;
                else s.advance = [{ when: 'timeout', ms }];
              })
            }
          />
          <span className="text-[11px] text-muted-foreground">ms</span>
        </Field>
      </Section>

      <p className="p-3 text-[12px] text-muted-foreground">
        Select a block on the canvas or in the layers tree to edit it.
      </p>
    </>
  );
}
